import { useCallback, useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, Pencil, Trash2, UserRoundPlus } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SmartImg } from "@/components/common/ui";
import { api, errorMessage } from "@/lib/api";

const empty = { name: "", title: "", picture_url: "" };

export const TeamPanel = () => {
  const [agents, setAgents] = useState([]);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const [editing, setEditing] = useState(null);
  const photoInput = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/agents");
      setAgents(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const clearPhoto = () => {
    set("picture_url", "");
    if (photoInput.current) photoInput.current.value = "";
  };
  const resetForm = () => { setForm(empty); setEditing(null); setPhotoError(""); if (photoInput.current) photoInput.current.value = ""; };
  const readPhoto = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size > 2 * 1024 * 1024) {
      setPhotoError("Choose a JPG, PNG, or WebP image under 2 MB.");
      event.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { set("picture_url", String(reader.result)); setPhotoError(""); };
    reader.readAsDataURL(file);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.picture_url) { setPhotoError("Add a headshot before saving."); return; }
    setSaving(true);
    try {
      const { data } = editing ? await api.put(`/admin/agents/${editing.id}`, form) : await api.post("/admin/agents", form);
      setAgents((current) => editing ? current.map((agent) => agent.id === data.id ? data : agent) : [data, ...current]);
      resetForm();
      toast.success(editing ? "Team member updated" : "Team member added");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  const beginEdit = (agent) => { setEditing(agent); setForm({ name: agent.name, title: agent.title, picture_url: agent.picture_url }); setPhotoError(""); if (photoInput.current) photoInput.current.value = ""; };
  const remove = async (agent) => {
    if (!window.confirm(`Remove ${agent.name} from the team?`)) return;
    try {
      await api.delete(`/admin/agents/${agent.id}`);
      setAgents((current) => current.filter((item) => item.id !== agent.id));
      toast.success("Team member removed");
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  return (
    <section className="border-y border-neutral-200 bg-white py-7 sm:py-9" data-testid="team-management-panel">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div><p className="eyebrow">Team roster</p><h2 className="mt-2 text-2xl text-ink" data-testid="team-management-heading">Meet the team</h2></div>
        <p className="text-sm text-neutral-500" data-testid="team-member-count">{agents.length} {agents.length === 1 ? "member" : "members"}</p>
      </div>
      <div className="grid gap-8 lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)]">
        <form onSubmit={submit} className="border border-neutral-200 p-5" data-testid="add-agent-form">
          <div className="flex items-center justify-between gap-2 text-ink"><span className="flex items-center gap-2"><UserRoundPlus className="h-5 w-5 text-brass-deep" /><h3 className="text-lg" data-testid="agent-form-heading">{editing ? "Edit team member" : "Add a team member"}</h3></span>{editing && <button type="button" onClick={resetForm} className="text-xs text-neutral-600 underline hover:text-ink" data-testid="agent-edit-cancel-button">Cancel</button>}</div>
          <div className="mt-5 space-y-4">
            <div className="space-y-1.5"><Label htmlFor="agent-name">Full name</Label><Input id="agent-name" value={form.name} onChange={(e) => set("name", e.target.value)} required maxLength={100} data-testid="agent-name-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="agent-title">Title</Label><Input id="agent-title" value={form.title} onChange={(e) => set("title", e.target.value)} required maxLength={100} data-testid="agent-title-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="agent-photo">Headshot</Label><Input ref={photoInput} id="agent-photo" type="file" accept="image/jpeg,image/png,image/webp" onChange={readPhoto} required={!form.picture_url} data-testid="agent-photo-input" /></div>
            {photoError && <p className="text-sm text-red-700" data-testid="agent-photo-error">{photoError}</p>}
            {form.picture_url && <div className="flex items-center gap-3" data-testid="agent-photo-preview"><SmartImg src={form.picture_url} alt="New agent preview" className="h-14 w-14 rounded-full object-cover" testId="agent-photo-preview-image" /><button type="button" onClick={clearPhoto} className="text-sm text-neutral-600 underline hover:text-ink" data-testid="agent-photo-remove-button">Remove photo</button></div>}
            <button type="submit" disabled={saving} className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-ink px-5 text-sm text-white transition-[background-color,transform] hover:bg-neutral-800 active:scale-[0.98] disabled:opacity-50" data-testid="agent-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}{editing ? "Save changes" : "Add team member"}</button>
          </div>
        </form>
        <div className="grid gap-3 sm:grid-cols-2" data-testid="agent-list">
          {loading && <p className="text-sm text-neutral-500" data-testid="agent-list-loading">Loading team…</p>}
          {!loading && !agents.length && <div className="border border-dashed border-neutral-300 p-7 text-center text-sm text-neutral-600" data-testid="agent-list-empty">Your team roster will appear here.</div>}
          {agents.map((agent) => <article key={agent.id} className="group relative overflow-hidden border border-neutral-200 bg-mist" data-testid={`agent-card-${agent.id}`}><SmartImg src={agent.picture_url} alt={agent.name} fallbackLabel={agent.name} className="aspect-[4/3] w-full object-cover" testId={`agent-photo-${agent.id}`} /><div className="p-4"><p className="text-base font-medium text-ink" data-testid={`agent-name-${agent.id}`}>{agent.name}</p><p className="mt-0.5 text-sm text-neutral-600" data-testid={`agent-title-${agent.id}`}>{agent.title}</p><div className="mt-4 flex gap-3"><button onClick={() => beginEdit(agent)} className="inline-flex items-center gap-1.5 text-xs text-neutral-600 transition-colors hover:text-ink" data-testid={`agent-edit-${agent.id}`}><Pencil className="h-3.5 w-3.5" /> Edit</button><button onClick={() => remove(agent)} className="inline-flex items-center gap-1.5 text-xs text-neutral-600 transition-colors hover:text-red-700" data-testid={`agent-delete-${agent.id}`}><Trash2 className="h-3.5 w-3.5" /> Remove</button></div></div></article>)}
        </div>
      </div>
    </section>
  );
};